import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Play, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { queryClient } from '@/lib/queryClient';
import { triggerCreditsGate } from '@/lib/credits-gate';
import { runVideoCloneGenerate, regenerateVideoCloneFrame } from './video-clone-runner';

/**
 * VideoScriptDoc — the editable video-clone script document, rendered inside
 * SidebarPanel (type 'video_script'). Loads the draft, lets the user edit the
 * user-editable script fields with a debounced PATCH autosave, and fires the
 * generate runner behind a cost-confirm modal.
 *
 * v5.10 UX: scene cards are COLLAPSED by default — one line each (Scene N —
 * Xs, original thumb + branded thumb, clip chip); clicking a row expands the
 * full editor (one expanded at a time). scene.originalFrameUrl is server-owned
 * (stamped at prepare) and renders as the "Theirs" half of the comparison.
 *
 * PATCH contract (server/routes/videoClone.ts): the script payload must
 * contain ONLY editable fields — {globalStyle, aspectRatio, scenes:[{index,
 * durationSec, visualPrompt, motionPrompt, voLine, onScreenText}]} with the
 * exact stored scene index set. startFrameUrl/clipUrl/clipStatus are
 * server-owned and must never be sent. aspectRatio is sent as the stored
 * value verbatim (not editable v1).
 */

// Matches the seeded seedance model_pricing row (credits per generated video
// second); ×1.1 is the estimate buffer shown before generating. Admin-only
// surface in v1 — a hardcoded constant is acceptable here.
// v5.65 — Seedance 2.5 at 1.4x markup (Dan, was 2.0x): 720p $0.36/s real
// → 56.6 credits/s. Must match the model_pricing row.
const CREDITS_PER_VIDEO_SECOND = 56.6;
const ESTIMATE_BUFFER = 1.1;
// Starter plan reference rate ($0.00900/credit) for the dollar hint.
const DOLLARS_PER_CREDIT = 0.009;

const SCENE_DURATION_MIN = 4;
const SCENE_DURATION_MAX = 15;

interface DraftScene {
  index: number;
  durationSec: number;
  /** v6.96 — which ≤30s generation window (Seedance pass) this scene renders
   *  in. Absent on pre-v6.96 drafts = single window. */
  generationWindow?: number;
  visualPrompt: string;
  motionPrompt: string;
  voLine: string;
  onScreenText: string;
  startFrameUrl: string | null;
  clipUrl: string | null;
  clipStatus: 'none' | 'generating' | 'done' | 'failed';
  // v5.10 server-owned (stamped at prepare, absent on pre-redesign drafts):
  // the SOURCE video's frame for this scene — the "Theirs" half of the
  // original-vs-branded comparison. Never sent back in the PATCH.
  originalFrameUrl?: string | null;
}

interface VideoCloneDraft {
  id: number;
  status: 'preparing' | 'ready' | 'generating' | 'stitching' | 'completed' | 'failed';
  projectId: number;
  analysis: { hook?: string; angle?: string; styleRead?: string } | null;
  script: { globalStyle: string; aspectRatio: string; scenes: DraftScene[] } | null;
  aspectRatio: string;
  totalDurationSec: number;
  finalVideoUrl: string | null;
  sourceTemplateId: number;
}

interface EditableScene {
  index: number;
  durationSec: number;
  visualPrompt: string;
  motionPrompt: string;
  voLine: string;
  onScreenText: string;
}

interface EditableScript {
  globalStyle: string;
  scenes: EditableScene[];
}

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const clampDuration = (v: number): number =>
  Math.max(SCENE_DURATION_MIN, Math.min(SCENE_DURATION_MAX, Math.round(v)));

// ─── Shared inline-style tokens (SidebarPanel conventions) ───────────────────
const labelStyle: CSSProperties = {
  fontSize: 10, fontWeight: 600, color: '#5F6D82', textTransform: 'uppercase',
  letterSpacing: '0.06em', display: 'block', marginBottom: 4,
};
const inputStyle: CSSProperties = {
  width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #DCE3ED',
  background: '#FFFFFF', fontSize: 12.5, color: '#1A1A1A', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5,
};
const textareaStyle: CSSProperties = { ...inputStyle, resize: 'vertical' as const };
// Mini thumbnails on the collapsed scene rows (original + branded, side by side).
const thumbMiniStyle: CSSProperties = {
  width: 26, height: 26, borderRadius: 5, border: '1px solid #DCE3ED',
  objectFit: 'cover', display: 'block', background: '#F1F5F9', flexShrink: 0,
};

function StatusChip({ text, tone }: { text: string; tone: 'ok' | 'warn' | 'error' | 'busy' }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    ok: { bg: '#DCFCE7', fg: '#166534' },
    warn: { bg: '#FEF9C3', fg: '#713F12' },
    error: { bg: '#FEE2E2', fg: '#991B1B' },
    busy: { bg: '#EEF2FF', fg: '#4338CA' },
  };
  const c = colors[tone];
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10.5, fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

export function VideoScriptDoc({ draftId, expandScene }: { draftId: number; expandScene?: number | null }) {
  const activeProjectId = useCanvasStore((s) => s.activeProjectId);

  const { data: draft, isLoading, error: loadError } = useQuery<VideoCloneDraft>({
    queryKey: ['/api/video-clone', draftId],
    queryFn: async () => {
      const res = await fetch(`/api/video-clone/${draftId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    // Poll while the server owns the draft (prepare storyboard phase, clip
    // generation, stitching) so frame thumbnails / clip chips / status stay
    // live even though the SSE stream is handled by the runner, not this doc.
    refetchInterval: (query) => {
      const st = query.state.data?.status;
      return st === 'preparing' || st === 'generating' || st === 'stitching' ? 4000 : false;
    },
  });

  const [edit, setEdit] = useState<EditableScript | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [regenningIndex, setRegenningIndex] = useState<number | null>(null);
  // v5.10 collapsed-by-default scene cards: one line each until clicked.
  // Single expanded card at a time keeps the doc scannable.
  // v5.13 — expandScene (canvas tile hover button) seeds the expanded card and
  // KEEPS steering it: the panel stays mounted across openSidebarPanel calls,
  // so clicking another scene's tile only changes the prop — the effect below
  // is what makes that click actually switch the expanded card. null means
  // "no specific scene" (the final tile) and leaves the current state alone.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(expandScene ?? null);
  useEffect(() => {
    if (typeof expandScene === 'number') setExpandedIndex(expandScene);
  }, [expandScene]);
  const [sceneErrors, setSceneErrors] = useState<Record<number, string>>({});
  // visualPrompt value at load / last successful regen, per scene — a frame is
  // "stale" once the current prompt differs while a frame image exists.
  const frameBaselineRef = useRef<Record<number, string>>({});
  const seededDraftIdRef = useRef<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed local editable state once per draftId (refetches must not clobber
  // in-progress edits; server-owned fields render straight from the query).
  useEffect(() => {
    if (!draft?.script || seededDraftIdRef.current === draft.id) return;
    seededDraftIdRef.current = draft.id;
    setEdit({
      globalStyle: draft.script.globalStyle,
      scenes: draft.script.scenes.map((s) => ({
        index: s.index,
        durationSec: s.durationSec,
        visualPrompt: s.visualPrompt,
        motionPrompt: s.motionPrompt,
        voLine: s.voLine ?? '',
        onScreenText: s.onScreenText ?? '',
      })),
    });
    frameBaselineRef.current = Object.fromEntries(draft.script.scenes.map((s) => [s.index, s.visualPrompt]));
    setSaveState('idle');
  }, [draft]);

  const status = draft?.status;
  const isPreparing = status === 'preparing';
  const isBusy = status === 'generating' || status === 'stitching';
  const isCompleted = status === 'completed';
  // 'preparing' locks too: the doc opens at vc_draft while storyboard frames
  // are still rendering, and prepare ends with a full script write that would
  // clobber concurrent edits (the PATCH route also 409s during prepare).
  const locked = isPreparing || isBusy || isCompleted;

  // ─── Debounced autosave (1.5s) — editable shape ONLY ───────────────────────
  // dirtyRef = "latestRef.current.edit holds changes not yet SUCCESSFULLY
  // saved". Only a successful save clears it (guarded by editSeqRef so a
  // success can't clear dirtiness from an edit made while the save was in
  // flight); a failed save keeps it set so the unmount flush retries instead
  // of silently dropping the edit.
  const dirtyRef = useRef(false);
  const editSeqRef = useRef(0);
  // Serialization guard: two concurrent PATCHes could land out of order (an
  // older payload overwriting newer server state). Every save waits out the
  // in-flight one first; flush paths await it directly instead of firing a
  // concurrent PATCH.
  const saveInFlightRef = useRef<Promise<boolean> | null>(null);

  const doSave = useCallback(async (script: EditableScript, storedAspectRatio: string): Promise<boolean> => {
    // while, not if: two callers awaiting the same settled promise would both
    // proceed at once — loop until the slot is actually free.
    while (saveInFlightRef.current) await saveInFlightRef.current.catch(() => false);
    // Captured AFTER the wait so it reflects edits made during earlier saves.
    const seqAtStart = editSeqRef.current;
    // Payload vintage must equal seq vintage: re-snapshot from latestRef so an
    // edit made during the wait above is carried by THIS save — the call-time
    // args predate the wait, and saving them while clearing seqAtStart's dirty
    // flag would silently drop that edit (Generate/unmount flush would skip).
    const fresh = latestRef.current.edit ?? script;
    const freshAspect = latestRef.current.aspect ?? storedAspectRatio;
    const run = (async (): Promise<boolean> => {
    setSaveState('saving');
    setSaveError(null);
    try {
      const res = await fetch(`/api/video-clone/${draftId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          script: {
            globalStyle: fresh.globalStyle,
            aspectRatio: freshAspect, // stored value verbatim — not editable v1
            scenes: fresh.scenes.map((s) => ({
              index: s.index,
              durationSec: clampDuration(s.durationSec),
              visualPrompt: s.visualPrompt,
              motionPrompt: s.motionPrompt,
              voLine: s.voLine,
              onScreenText: s.onScreenText,
            })),
          },
        }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error) msg = String(body.error);
        } catch { /* keep status message */ }
        setSaveState('error');
        setSaveError(res.status === 409 ? 'Generation in progress — edits are locked.' : msg);
        return false;
      }
      const updated = await res.json();
      queryClient.setQueryData(['/api/video-clone', draftId], updated);
      if (editSeqRef.current === seqAtStart) dirtyRef.current = false;
      // An edit made while this save was in flight must not show a false
      // "Saved" — it is still pending its own debounce.
      setSaveState(editSeqRef.current === seqAtStart ? 'saved' : 'dirty');
      return true;
    } catch (e) {
      console.error('[VideoScriptDoc] save failed:', e);
      setSaveState('error');
      setSaveError('Save failed — check your connection.');
      return false;
    }
    })();
    saveInFlightRef.current = run;
    try {
      return await run;
    } finally {
      if (saveInFlightRef.current === run) saveInFlightRef.current = null;
    }
  }, [draftId]);

  const latestRef = useRef<{ edit: EditableScript | null; aspect: string }>({ edit: null, aspect: '9:16' });
  latestRef.current = { edit, aspect: draft?.script?.aspectRatio ?? draft?.aspectRatio ?? '9:16' };

  const markEdit = useCallback((updater: (prev: EditableScript) => EditableScript) => {
    setEdit((prev) => (prev ? updater(prev) : prev));
    setSaveState('dirty');
    dirtyRef.current = true;
    editSeqRef.current++;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { edit: latest, aspect } = latestRef.current;
      if (latest) void doSave(latest, aspect);
    }, 1500);
  }, [doSave]);

  // Unmount: FLUSH a pending debounced save instead of dropping it — closing
  // the panel inside the 1.5s window must not silently lose the edit.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (dirtyRef.current && latestRef.current.edit) {
      void doSave(latestRef.current.edit, latestRef.current.aspect);
    }
  }, [doSave]);

  const updateScene = (index: number, patch: Partial<EditableScene>) =>
    markEdit((prev) => ({
      ...prev,
      scenes: prev.scenes.map((s) => (s.index === index ? { ...s, ...patch } : s)),
    }));

  // ─── Regenerate one scene's start frame ────────────────────────────────────
  const regenFrame = async (index: number) => {
    if (regenningIndex !== null || locked) return;
    setRegenningIndex(index);
    setSceneErrors((prev) => ({ ...prev, [index]: '' }));
    try {
      // Flush any pending prompt edit first — the server renders the frame
      // from the STORED visualPrompt. Wait out an in-flight save, then re-save
      // if edits are still unsaved (dirtyRef is the truth here; the render-
      // scoped saveState is stale inside this async handler).
      if (saveInFlightRef.current) {
        const ok = await saveInFlightRef.current.catch(() => false);
        if (!ok && !dirtyRef.current) { setRegenningIndex(null); return; }
      }
      if (dirtyRef.current && latestRef.current.edit) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const ok = await doSave(latestRef.current.edit, latestRef.current.aspect);
        if (!ok) { setRegenningIndex(null); return; }
      }
      // v5.71 — regen mirrors onto the CANVAS (grey loading box at the
      // frame's slot, old tile swapped for the new frame) via the runner
      // helper; falls back to the plain endpoint if no project is resolvable.
      const projectId = draft?.projectId ?? activeProjectId;
      const aspect = draft?.script?.aspectRatio ?? draft?.aspectRatio ?? '9:16';
      if (projectId) {
        await regenerateVideoCloneFrame(draftId, index, projectId, aspect);
      } else {
        // v5.73 — the route answers 202 and works in the background; the
        // doc's "preparing" status poll picks up the result.
        const res = await fetch(`/api/video-clone/${draftId}/scenes/${index}/regen-frame`, { method: 'POST' });
        if (!res.ok) {
          const err = new Error(`HTTP ${res.status}`) as Error & { status?: number; body?: Record<string, unknown> | null };
          err.status = res.status;
          try { err.body = await res.json(); } catch { err.body = null; }
          throw err;
        }
      }
      // Frame regenerated from the current stored prompt — reset the baseline.
      const currentPrompt = latestRef.current.edit?.scenes.find((s) => s.index === index)?.visualPrompt;
      if (currentPrompt !== undefined) frameBaselineRef.current[index] = currentPrompt;
      queryClient.invalidateQueries({ queryKey: ['/api/video-clone', draftId] });
    } catch (e) {
      console.error('[VideoScriptDoc] regen-frame failed:', e);
      const err = e as Error & { status?: number; body?: Record<string, unknown> | null };
      if (err.status === 402 && err.body?.code === 'CREDITS_EXHAUSTED') {
        triggerCreditsGate(Number(err.body.balance ?? 0), Number(err.body.needed ?? 0));
        setSceneErrors((prev) => ({ ...prev, [index]: 'Not enough credits.' }));
      } else if (err.status) {
        setSceneErrors((prev) => ({ ...prev, [index]: err.message || `Regen failed (HTTP ${err.status})` }));
      } else {
        setSceneErrors((prev) => ({ ...prev, [index]: 'Regen failed — check your connection.' }));
      }
    } finally {
      setRegenningIndex(null);
    }
  };

  // ─── Generate ──────────────────────────────────────────────────────────────
  const rawDuration = edit
    ? edit.scenes.reduce((sum, s) => sum + clampDuration(s.durationSec), 0)
    : draft?.totalDurationSec ?? 0;
  // v7.13 — PER-WINDOW duration + billing (was: Math.min(30, rawDuration), a
  // leftover of the v5.58 single-pass era). A source longer than one Seedance
  // pass now renders as up to 3 windows of ≤30s each and is stitched, so the
  // old cap under-reported a 73s script as "30s total" AND under-estimated
  // the credits by the same factor — the user approved a spend ~2.4x below
  // what the run actually bills. Mirror the server: each window bills
  // min(30, its own scene sum); the ad's length is their total.
  const windowTotals = (() => {
    const scenes = draft?.script?.scenes ?? [];
    const byWindow = new Map<number, number>();
    for (const sc of scenes) {
      const w = sc.generationWindow ?? 0;
      // Prefer the user's edited duration for this scene when one exists.
      const edited = edit?.scenes.find((e) => e.index === sc.index)?.durationSec;
      const dur = clampDuration(edited ?? sc.durationSec);
      byWindow.set(w, (byWindow.get(w) ?? 0) + dur);
    }
    return Array.from(byWindow.entries()).sort((a, b) => a[0] - b[0]).map(([, total]) => total);
  })();
  const partCount = Math.max(1, windowTotals.length);
  // Billed/rendered seconds — what the server will actually generate.
  const totalDuration = windowTotals.length > 0
    ? windowTotals.reduce((sum, t) => sum + Math.min(30, t), 0)
    : Math.min(30, rawDuration);
  const creditEstimate = Math.ceil(totalDuration * CREDITS_PER_VIDEO_SECOND * ESTIMATE_BUFFER);
  const dollarEstimate = (creditEstimate * DOLLARS_PER_CREDIT).toFixed(2);

  const handleGenerateConfirmed = async () => {
    setShowConfirm(false);
    // Flush pending edits so generation runs the script the user sees: wait
    // out an in-flight save, then re-save if edits are still unsaved
    // (dirtyRef is the truth — the render-scoped saveState is stale here).
    if (saveInFlightRef.current) {
      const ok = await saveInFlightRef.current.catch(() => false);
      if (!ok && !dirtyRef.current) return;
    }
    if (dirtyRef.current && latestRef.current.edit) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const ok = await doSave(latestRef.current.edit, latestRef.current.aspect);
      if (!ok) return;
    }
    const projectId = draft?.projectId ?? activeProjectId;
    if (!projectId) return;
    runVideoCloneGenerate(draftId, projectId).catch((e) => console.error('[VideoScriptDoc] generate failed:', e));
    // v6.63 — Generate hands the stage back to the canvas (Dan: "it should
    // auto close this popover to show the canvas and it generating"): the
    // loading box + progress live there, not in this panel.
    useCanvasStore.getState().closeSidebarPanel();
    // Pick up the server-side 'generating' status (kicks off the poll loop).
    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['/api/video-clone', draftId] }), 1500);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 8, color: '#5F6D82', fontSize: 13 }}>
        <Loader2 size={16} className="animate-spin" /> Loading script…
      </div>
    );
  }
  if (loadError || !draft) {
    return (
      <div style={{ padding: '48px 28px', textAlign: 'center', color: '#991B1B', fontSize: 13 }}>
        Could not load the video script draft.
      </div>
    );
  }

  const scenes = draft.script?.scenes ?? [];
  const failedScenes = scenes.filter((s) => s.clipStatus === 'failed').length;
  // v5.38 — review-then-stitch: clip runs stop before the stitch; when every
  // clip is done the button becomes the FREE stitch call (the server's
  // stitch-only resume skips all credit gates).
  const allClipsDone = scenes.length > 0 && scenes.every((s) => s.clipStatus === 'done');
  const generateLabel =
    status === 'generating' ? 'Generating video…'
    : status === 'stitching' ? 'Finalizing…'
    : status === 'preparing' ? 'Preparing storyboard…'
    : failedScenes > 0 ? 'Retry video generation'
    : allClipsDone ? 'Create Final Ad'
    : 'Generate';
  // locked already covers preparing/generating/stitching/completed.
  const generateDisabled = locked || !edit;

  const saveChip =
    saveState === 'saving' ? { text: 'Saving…', tone: 'busy' as const }
    : saveState === 'saved' ? { text: 'Saved', tone: 'ok' as const }
    : saveState === 'dirty' ? { text: 'Unsaved edits', tone: 'warn' as const }
    : saveState === 'error' ? { text: saveError || 'Save failed', tone: 'error' as const }
    : null;

  return (
    <div data-testid="video-script-doc" style={{ padding: '18px 22px', fontSize: 13, color: '#334155' }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 16 }}>
        {draft.analysis?.hook && (
          <div data-testid="video-doc-hook" style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', lineHeight: 1.4 }}>
            {draft.analysis.hook}
          </div>
        )}
        {draft.analysis?.angle && (
          <div data-testid="video-doc-angle" style={{ fontSize: 12.5, color: '#526074', marginTop: 4, lineHeight: 1.5 }}>
            {draft.analysis.angle}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <StatusChip text={draft.script?.aspectRatio ?? draft.aspectRatio} tone="busy" />
          <StatusChip text={partCount > 1 ? `${totalDuration}s total · ${partCount} parts` : `${totalDuration}s total`} tone="busy" />
          <span data-testid="video-doc-estimate" style={{ fontSize: 11.5, color: '#526074' }}>
            ~{creditEstimate.toLocaleString()} credits (≈${dollarEstimate})
          </span>
          {saveChip && (
            <span data-testid="video-doc-save-chip" style={{ marginLeft: 'auto' }}>
              <StatusChip text={saveChip.text} tone={saveChip.tone} />
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button
            data-testid="video-doc-generate"
            // v5.38 — stitching already-paid clips is FREE: no credit-confirm
            // modal, straight to the stitch-only generate call.
            onClick={() => (allClipsDone ? handleGenerateConfirmed() : setShowConfirm(true))}
            disabled={generateDisabled}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8,
              border: 'none', background: generateDisabled ? '#DCE3ED' : '#1A1A1A',
              color: generateDisabled ? '#94A3B8' : '#FFFFFF', fontSize: 13, fontWeight: 600,
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {isBusy || status === 'preparing' ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {isCompleted ? 'Completed' : generateLabel}
          </button>
          {(isPreparing || isBusy) && (
            <span data-testid="video-doc-phase-note" style={{ fontSize: 12, color: '#526074' }}>
              {isPreparing
                ? 'Storyboard rendering — editing unlocks when it\'s done.'
                : status === 'stitching' ? 'Stitching the final ad…' : 'Clips are generating — editing is locked.'}
            </span>
          )}
          {allClipsDone && status === 'ready' && (
            <span data-testid="video-doc-review-note" style={{ fontSize: 12, color: '#526074' }}>
              Video ready — hover it on the canvas to review video + audio (regenerate from the tile), then finalize. Free.
            </span>
          )}
        </div>

        {isCompleted && draft.finalVideoUrl && (
          <div style={{ marginTop: 14, border: '1px solid #DCE3ED', borderRadius: 12, overflow: 'hidden' }}>
            <video
              src={draft.finalVideoUrl}
              controls
              data-testid="video-doc-final-video"
              style={{ width: '100%', maxHeight: 320, display: 'block', background: '#000' }}
            />
            <div style={{ padding: '8px 12px', fontSize: 12 }}>
              <a href={draft.finalVideoUrl} target="_blank" rel="noreferrer" style={{ color: '#4338CA', fontWeight: 600, textDecoration: 'none' }}>
                Open final video
              </a>
            </div>
          </div>
        )}
      </div>

      {/* ── Global style ── */}
      {edit && (
        <fieldset disabled={locked} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Global style</label>
            <textarea
              data-testid="video-doc-global-style"
              value={edit.globalStyle}
              onChange={(e) => markEdit((prev) => ({ ...prev, globalStyle: e.target.value }))}
              rows={3}
              style={textareaStyle}
            />
          </div>

          {/* ── Scene cards ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {edit.scenes.map((scene) => {
              const stored = scenes.find((s) => s.index === scene.index);
              const isStale =
                !!stored?.startFrameUrl &&
                frameBaselineRef.current[scene.index] !== undefined &&
                scene.visualPrompt !== frameBaselineRef.current[scene.index];
              const originalUrl = stored?.originalFrameUrl || null;
              // v5.60 (Dan: "clean it up") — single-pass world: cards are
              // ALWAYS expanded (the whole storyboard is visible at once),
              // per-scene clip chips are gone (the video is all-or-nothing),
              // and only scene 1 carries the frame pair + first-frame editor.
              return (
                <div
                  key={scene.index}
                  data-testid={`video-doc-scene-${scene.index}`}
                  style={{ border: '1px solid #DCE3ED', borderRadius: 12, background: '#F7F9FC', padding: '12px 14px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                      {scene.index === 0
                        ? `Scene 1 · opening frame — ${clampDuration(scene.durationSec)}s`
                        : `Scene ${scene.index + 1} — ${clampDuration(scene.durationSec)}s`}
                    </span>
                  </div>

                  {true && (
                  <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 10 }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Seconds</label>
                    <input
                      data-testid={`video-doc-scene-${scene.index}-duration`}
                      type="number"
                      min={SCENE_DURATION_MIN}
                      max={SCENE_DURATION_MAX}
                      step={1}
                      value={scene.durationSec}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v)) updateScene(scene.index, { durationSec: v });
                      }}
                      onBlur={() => updateScene(scene.index, { durationSec: clampDuration(scene.durationSec) })}
                      style={{ ...inputStyle, width: 64, padding: '5px 8px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    {/* Scene 1: Theirs + Yours frames + regen (the @Image 1
                        anchor). Later scenes: a small source thumb only —
                        their frames aren't generated in the single-pass world. */}
                    {scene.index > 0 && originalUrl && (
                      <div style={{ flexShrink: 0, width: 56 }}>
                        <label style={labelStyle}>Theirs</label>
                        <img
                          src={originalUrl}
                          alt={`Scene ${scene.index + 1} original frame`}
                          style={{ width: 56, borderRadius: 6, border: '1px solid #DCE3ED', display: 'block' }}
                        />
                      </div>
                    )}
                    {scene.index === 0 && (
                    <div style={{ flexShrink: 0, width: originalUrl ? 164 : 96 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {originalUrl && (
                          <div style={{ width: 78 }}>
                            <label style={labelStyle}>Theirs</label>
                            <img
                              src={originalUrl}
                              alt={`Scene ${scene.index + 1} original frame`}
                              style={{ width: 78, borderRadius: 8, border: '1px solid #DCE3ED', display: 'block' }}
                            />
                          </div>
                        )}
                        <div style={{ width: originalUrl ? 78 : 96 }}>
                          {originalUrl && <label style={labelStyle}>Yours</label>}
                          {stored?.startFrameUrl ? (
                            <img
                              src={stored.startFrameUrl}
                              alt={`Scene ${scene.index + 1} frame`}
                              style={{ width: originalUrl ? 78 : 96, borderRadius: 8, border: '1px solid #DCE3ED', display: 'block' }}
                            />
                          ) : (
                            <div style={{ width: originalUrl ? 78 : 96, height: 96, borderRadius: 8, border: '1px dashed #C8D3E2', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 10.5, color: '#5F6D82', padding: 6, boxSizing: 'border-box' }}>
                              {/* v5.58 single-pass: only scene 1 gets a frame. */}
                              {scene.index > 0 ? 'No frame needed — single-pass video' : status === 'preparing' ? 'Frame generating…' : 'Frame failed'}
                            </div>
                          )}
                        </div>
                      </div>
                      {scene.index === 0 && (
                        <button
                          data-testid={`video-doc-scene-${scene.index}-regen`}
                          onClick={() => void regenFrame(scene.index)}
                          disabled={locked || regenningIndex !== null}
                          style={{
                            marginTop: 6, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                            padding: '5px 6px', borderRadius: 6, border: '1px solid #DCE3ED',
                            background: '#FFFFFF', color: locked || regenningIndex !== null ? '#94A3B8' : '#334155',
                            fontSize: 10.5, fontWeight: 600, cursor: locked || regenningIndex !== null ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {regenningIndex === scene.index
                            ? <Loader2 size={10} className="animate-spin" />
                            : <RefreshCw size={10} />}
                          {stored?.startFrameUrl ? 'Regenerate frame' : 'Generate frame'}
                        </button>
                      )}
                      {scene.index === 0 && (
                        // v5.58 single-pass — scene 1's frame is @Image 1: the
                        // identity + product anchor for the WHOLE generation.
                        <div data-testid="video-doc-scene-0-cast-note" style={{ marginTop: 5, fontSize: 10, color: '#5F6D82', lineHeight: 1.35 }}>
                          Scene 1's frame anchors the whole video — the product and person in it carry through every cut.
                        </div>
                      )}
                      {isStale && (
                        <div data-testid={`video-doc-scene-${scene.index}-stale`} style={{ marginTop: 5, display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 10, color: '#713F12', lineHeight: 1.35 }}>
                          <AlertTriangle size={10} style={{ flexShrink: 0, marginTop: 1 }} />
                          frame is stale — regenerate?
                        </div>
                      )}
                      {sceneErrors[scene.index] && (
                        <div style={{ marginTop: 5, fontSize: 10, color: '#991B1B', lineHeight: 1.35 }}>
                          {sceneErrors[scene.index]}
                        </div>
                      )}
                    </div>
                    )}

                    {/* Editable fields — the storyboard window. First-frame
                        prompt only exists on scene 1 (it IS @Image 1). */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {scene.index === 0 && (
                      <div>
                        <label style={labelStyle}>First frame</label>
                        <textarea
                          data-testid={`video-doc-scene-${scene.index}-visual`}
                          value={scene.visualPrompt}
                          onChange={(e) => updateScene(scene.index, { visualPrompt: e.target.value })}
                          rows={3}
                          style={textareaStyle}
                        />
                      </div>
                      )}
                      <div>
                        <label style={labelStyle}>Action</label>
                        <textarea
                          data-testid={`video-doc-scene-${scene.index}-motion`}
                          value={scene.motionPrompt}
                          onChange={(e) => updateScene(scene.index, { motionPrompt: e.target.value })}
                          rows={2}
                          style={textareaStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Spoken line</label>
                        <textarea
                          data-testid={`video-doc-scene-${scene.index}-vo`}
                          value={scene.voLine}
                          onChange={(e) => updateScene(scene.index, { voLine: e.target.value })}
                          rows={2}
                          style={textareaStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>On-screen text (post-production — not rendered in the video)</label>
                        <input
                          data-testid={`video-doc-scene-${scene.index}-ost`}
                          value={scene.onScreenText}
                          onChange={(e) => updateScene(scene.index, { onScreenText: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
      )}

      {!edit && !isLoading && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
          The script is still being written…
        </div>
      )}

      {/* ── Generate confirm modal ── */}
      {showConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowConfirm(false)}
        >
          <div
            data-testid="video-doc-confirm-modal"
            style={{ background: '#FFFFFF', border: '1px solid #DCE3ED', borderRadius: 14, width: 360, padding: '20px 22px', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 14.5, fontWeight: 700, color: '#1A1A1A', marginBottom: 8 }}>
              Generate this video ad?
            </div>
            <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6, marginBottom: 4 }}>
              {totalDuration}s video — {edit?.scenes.length ?? 0} scenes{partCount > 1
                ? ` generated as ${partCount} parts (one Seedance pass each), then stitched into one ad`
                : ' generated in ONE Seedance pass'} — estimated{' '}
              <strong>{creditEstimate.toLocaleString()} credits</strong> (≈${dollarEstimate}).
            </div>
            <div style={{ fontSize: 11.5, color: '#5F6D82', lineHeight: 1.5, marginBottom: 16 }}>
              The actual charge is per completed second — a failed generation is not billed and can be retried.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #DCE3ED', background: 'transparent', color: '#526074', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                data-testid="video-doc-confirm-generate"
                onClick={() => void handleGenerateConfirmed()}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
